import { Component, OnInit, ViewChild, type ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { trigger, transition, style, animate, query, stagger } from '@angular/animations';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { CookieService } from 'ngx-cookie-service';
import * as XLSX from 'xlsx';
import { environment } from 'src/environments/environment';
import { FullLoaderService } from 'src/app/shared/services/full-loader.service';
import { SupplierServiceInvoiceListService } from '../supplier-service-invoice-list.service';
import { SettingsService } from 'src/app/views/settings/settings.service';
import * as Excel from 'exceljs';
import * as FileSaver from 'file-saver';

const labelMap: Record<string, string> = {
  name: 'Name',
  address: 'Address',
  contact: 'Contact',
  phone: 'Phone',
  fax: 'Fax',
  email: 'Email',
  billingAddress: 'Billing Address',
  mpsBuyerCode: 'MPS Buyer Code',
  mpsSupplierCode: 'MPS Supplier Code',
  PIC: 'PIC',
  vat: 'VAT'

};

@Component({
  selector: 'app-supplier-service-invoices-list',
  templateUrl: './supplier-service-invoices-list.component.html',
  styleUrls: ['./supplier-service-invoices-list.component.css']
  ,
  animations: [
    // Page slides in from bottom of the viewport on load
    trigger('pageFadeUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(100vh)' }),
        animate('1200ms cubic-bezier(.22,1,.36,1)', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    // Staggered fade-up for child items with class .stagger-item
    trigger('staggerFadeUp', [
      transition(':enter', [
        query('.stagger-item', [
          style({ opacity: 0, transform: 'translateY(13px)' }),
          stagger(80, [
            animate('350ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})
export class SupplierServiceInvoicesListComponent implements OnInit {
  @ViewChild('archivedSection', { static: false }) archivedSection!: ElementRef;

  // Basic context
  userId!: number;
  userRole!: string;
  get userIdAsString(): string { return (this.userId ?? '').toString(); }

  // Loader + chat (to mirror PO UI)
  showLoader = false;
  isChatOpen = false;
  headerData: any;

  currencyCode = '';

  // Filters/search/sort/pagination
  searchParam = '';
  fromDate: any;
  toDate: any;
  limit = 10;
  offset = 0;
  totalRecords = 0;
  sortField = '';
  sortDirection = '';

  archivedLimit = 10;
  archivedOffset = 0;
  archivedTotalRecords = 0;
  archivedSortField = '';
  archivedSortDirection = '';

  // Status filter UI
  activeIndex = 0;
  selectedStatus = 'ALL';
  dropdownOpen = false;
  invoiceTypeDropdownOpen = false;

  filterStatuses = [
    { name: 'Paid', label: 'Invoice', subtext: '0/USD00' },
    { name: 'Unpaid', label: 'Invoice', subtext: '0/USD00' },
    { name: 'Overdue', label: 'Invoice', subtext: '0/USD00' },
    { name: 'ALL', label: 'Invoice' },
    { name: 'Dispatched', label: 'Dispatched' },
    { name: 'Delivered', label: 'Delivered' },
  ];

  filterStatusesRfqAndQuote = [
    { name: 'Acknowledged', label: 'RFQ' },
    { name: 'Declined', label: 'RFQ' },
    { name: 'Lost', label: 'RFQ' },
    { name: 'Lapsed', label: 'RFQ' },
    { name: 'Unactioned', label: 'RFQ' },
    { name: 'Accepted', label: 'Quote' },
    { name: 'Declined', label: 'Quote' },
    { name: 'Draft', label: 'Quote' },
    { name: 'Quoted', label: 'Quote' },
    { name: 'Withdrawn', label: 'Quote' },
  ];

  // Column picker
  items = [
    { label: 'supplier Dispatch Ref No', prop: 'supplierDispatchRefNo', selected: false },
    // { label: 'Invoice Date', prop: 'invoiceDate', selected: false },
    // { label: 'Due Date', prop: 'dueDate', selected: false },
    // { label: 'Port Name', prop: 'portName', selected: false },
  ];
  archivedItems = [
    { label: 'supplier Dispatch Ref No', prop: 'supplierDispatchRefNo', selected: false },
    // { label: 'Invoice Date', prop: 'invoiceDate', selected: false },
    // { label: 'Due Date', prop: 'dueDate', selected: false },
    // { label: 'Port Name', prop: 'portName', selected: false },
  ];
  selectedColumns: { label: string; prop: string }[] = [];
  archivedSelectedColumns: { label: string; prop: string }[] = [];

  // Data
  documentData: any[] = [];
  archivedDocumentData: any[] = [];

  // Selection
  selectedInvoices: number[] = [];
  selectedArchivedInvoices: number[] = [];

  // Expanders
  isArchivedExpand = false;

  // Modal state for "Mark Invoice as Paid"
  isMarkInvoicePaidModalOpen = false;
  modalInvoiceContext: any | null = null;
  markInvoiceAsPaidForm!: FormGroup;

  statusColors: { [key: string]: string } = {
    Accepted: '#0D8000',
    Pending: '#FFA600',
    Draft: '#003BFF',
    Rejected: '#FF0000',
    Paid: '#0D8000',
    Unpaid: '#FFA600',
    Overdue: '#FF0000',
  };

  showContactPopup:boolean = false;
  selectedBuyer: any = {
    companyName: 'N/A',
    contactPerson: 'N/A',
    email: 'N/A',
    phone: 'N/A',
    address: 'N/A',
    fax:'N/A'

  };

  constructor(
    private router: Router,
    private snack: MatSnackBar,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private cookieService: CookieService,
    private fullLoaderService: FullLoaderService,
    private invoiceListService: SupplierServiceInvoiceListService,
    private fb: FormBuilder,
    private settingsService: SettingsService
  ) {
    this.iconRegistry.addSvgIcon(
      'downloadExcel',
      this.sanitizer.bypassSecurityTrustResourceUrl(
        `${environment.assetsLink}images/excel.svg`
      )
    );
  }

  async ngOnInit(): Promise<void> {
    const uid = this.cookieService.get('userId');
    this.userId = uid ? Number(uid) : 0;
    this.userRole = this.cookieService.get('userRole');
    // Ensure ALL is selected by default on load
    const allIndex = this.filterStatuses.findIndex(s => s.name === 'ALL');
    this.activeIndex = allIndex >= 0 ? allIndex : 0;
    this.selectedStatus = 'ALL';

    // Fetch supplier currency first so fallbacks can use it
    await this.fetchCurrencyCode();

    // Fetch invoice status summary (counts and totals) for supplier
    await this.fetchInvoiceStatusSummaryBySupplierId();

    // Fetch invoices from API
    this.fetchInvoiceLists();

    // Initialize Mark Invoice As Paid form structure
    this.markInvoiceAsPaidForm = this.fb.group({
      invoiceRefNumber: [{ value: '', disabled: true }],
      supplierCompanyName: [{ value: '', disabled: true }],
      amountDue: [{ value: '', disabled: true }],

      paymentDate: [null, Validators.required],
      amountPaid: [null, Validators.required],
      paymentMethod: ['', Validators.required],
      transactionRefNumber: ['', Validators.required],
      fromAccount: [''],
      toAccount: [''],
      notes: [''],
      paymentProof: [null, Validators.required],
    });

    this.markInvoiceAsPaidForm?.disable();
  }

  private async fetchCurrencyCode(): Promise<void> {
    try {
      const supplierId = Number(this.cookieService.get('userId')) || this.userId || 0;
      const response = await this.settingsService.fetchSupplierSetup('TransactionSetup', supplierId);
      const code = (response?.[0]?.currencyCode || '').toString().trim();
      this.currencyCode = code || 'USD';
    } catch (e) {
      console.error('Failed to fetch supplier currency setup', e);
      // Sensible default to avoid empty currency in UI fallbacks
      this.currencyCode = this.currencyCode || 'USD';
    }
  }

  async fetchInvoiceStatusSummaryBySupplierId() {
    try {
      const reqBody: any = {
        supplierId: Number(this.cookieService.get('userId')) || this.userId || 0
      };

      const result = await this.invoiceListService.fetchSupplierServiceInvoiceStatusSummary(reqBody);

      if (result?.success && Array.isArray(result.statusSummary)) {
        const summaryMap = new Map<string, string>(
          result.statusSummary.map((item: any) => [item.name, item.subtext])
        );

        this.filterStatuses = this.filterStatuses.map((status) => {
          if (summaryMap.has(status.name)) {
            return { ...status, subtext: summaryMap.get(status.name) || '' };
          } else if (['Paid', 'Unpaid', 'Overdue'].includes(status.name)) {
            // Fallback subtext for missing status
            console.log("currencyCode",this.currencyCode);
            return { ...status, subtext: `0/${this.currencyCode || 'USD'}00` };
          }
          return status;
        });
      }
    } catch (error) {
      console.error('Error fetching supplier invoice status summary:', error);
    }
  }

  // Fetch invoices list for supplier and map to table schema
  async fetchInvoiceLists(): Promise<void> {
    try {
      this.showLoader = true;

      const supplierId = Number(this.cookieService.get('userId')) || this.userId || 0;
      // Compute API pagination (ngx-datatable offset is page index)
      const pageIndex = Number(this.offset) || 0;
      const pageSize = Number(this.limit) || 10;
      const recordOffset = pageIndex * pageSize;

      // Format dates to YYYY-MM-DD as expected by backend
      const formatToYMD = (d: any) => {
        if (!d) return null;
        const date = new Date(d);
        if (isNaN(date.getTime())) return null;
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };

      const reqBody: any = {
        supplierId,
        limit: pageSize,
        offset: recordOffset,
        searchTerm: (this.searchParam || '').toString().trim(),
        sortField: (this.sortField || '').toString().trim(),
        sortDirection: (this.sortDirection || '').toString().trim(),
        fromDate: formatToYMD(this.fromDate),
        toDate: formatToYMD(this.toDate),
        docStatus: this.getDocStatusFilter(),
      };

      const response: any = await this.invoiceListService.fetchSupplierServiceInvoiceLists(reqBody);

      const list = Array.isArray(response?.invoicesList) ? response.invoicesList : [];

      console.log('Fetched invoices list from API before modification of status :', list);

      this.documentData = list.map((inv: any) => {
        const due = inv?.dueDate ? new Date(inv.dueDate) : null;
        const today = new Date();
        const defaultStatus = due && !isNaN(due.getTime()) && due < new Date(today.getFullYear(), today.getMonth(), today.getDate())
          ? 'Overdue'
          : 'Unpaid';

        const payment = inv?.invoicePaymentDetails || null;
        const isPaid = Number(payment?.isPaid) === 1;
        const finalStatus = isPaid ? 'Paid' : (inv?.invoiceStatus ?? defaultStatus);

        return {
          invoiceId: inv?.invoiceId ?? null,
          invoiceRefNumber: inv?.invoiceNumber ?? '-',
          purchaseOrderId: inv?.purchaseOrderId ?? inv?.poId ?? null,
          purchaseOrderRefNumber: inv?.purchaseOrderRefNumber ?? '-',
          rfqId: inv?.rfqId ?? null,
          rfqRefNumber: inv?.rfqRefNumber ?? '-',
          quoteId: inv?.quoteId ?? inv?.quotationId ?? null,
          quoteRefNumber: inv?.quoteRefNumber ?? '-',
          buyerName: inv?.buyerCompanyName ?? '-',
          buyerId: inv?.buyerId ?? null,
          supplierCompanyName: inv?.supplierCompanyName ?? '-',
          supplierName: inv?.supplierName ?? '-',
          supplierCompanyEmail: inv?.supplierCompanyEmail ?? '-',
          amount: inv?.amount ?? null,
          invoiceDate: inv?.invoiceDate ?? null,
          dueDate: inv?.dueDate ?? null,
          invoiceStatus: finalStatus,
          currency: inv?.currency ?? null,
          invoicePaymentDetails: payment,
          isDispatched: inv?.isDispatched ?? false,
          dispatchId: inv?.dispatchId ?? null,
          dispatchStatus : inv?.isDispatched ? "Dispatched" : "Pending",
          isDelivered: inv?.isDelivered ?? false,
          receiptId: inv?.receiptId ?? null,
          deliveryStatus : inv?.isDelivered ? "Delivered" : "Pending",
          companyEmailId: inv?.companyEmailId ?? null,
          buyerCompanyPhoneNumber: inv?.buyerCompanyPhoneNumber ?? null,
          buyerCompanyFax: inv?.buyerCompanyFax ?? null,
          buyerCompanyAddress: inv?.buyerCompanyAddress ?? null,
          buyerContact: inv?.buyerContact ?? null,
          buyerCompanyName: inv?.buyerCompanyName ?? null,
          poId: inv?.poId ?? null,
          supplierId:inv?.supplierId ?? null,
          supplierDispatchRefNo:inv?.supplierDispatchRefNo ?? null,
        } as any;
      });

      console.log('Fetched invoices list after modification of status :', this.documentData);

      // Total records should come from backend count
      const total = Number(response?.countData?.totalCount ?? 0);
      this.totalRecords = isNaN(total) ? this.documentData.length : total;
    } catch (error) {
      console.error('Failed to fetch invoices list', error);
      this.snack.open('Failed to fetch invoices list', 'OK', { duration: 4000 });
    } finally {
      this.showLoader = false;
    }
  }

  private seedDummyData() {
    this.documentData = [
      {
        invoiceId: 1001,
        invoiceRefNumber: 'INV-VSMPS-20251020-001',
        purchaseOrderRefNumber: 'PO-VSMPS-20251022-1001',
        rfqRefNumber: 'RFQ-VSMPS-20251022-5001',
        quoteRefNumber: 'Q-VSMPS-20251022-3001',
        buyerName: 'Acme Corp',
        invoiceStatus: 'Paid',
        invoiceDate: '2025-10-20',
        dueDate: '2025-11-15',
        amount: 11000,
      },
      {
        invoiceId: 1002,
        invoiceRefNumber: 'INV-VSMPS-20251021-002',
        purchaseOrderRefNumber: 'PO-VSMPS-20251022-1002',
        rfqRefNumber: 'RFQ-VSMPS-20251022-5002',
        quoteRefNumber: 'Q-VSMPS-20251022-3002',
        buyerName: 'Globex Ltd',
        invoiceStatus: 'Unpaid',
        invoiceDate: '2025-10-21',
        dueDate: '2025-11-10',
        amount: 10000,
      },
      {
        invoiceId: 1003,
        invoiceRefNumber: 'INV-VSMPS-20251022-003',
        purchaseOrderRefNumber: 'PO-VSMPS-20251022-1003',
        rfqRefNumber: 'RFQ-VSMPS-20251022-5003',
        quoteRefNumber: 'Q-VSMPS-20251022-3003',
        buyerName: 'Initech',
        invoiceStatus: 'Overdue',
        invoiceDate: '2025-10-10',
        dueDate: '2025-10-20',
        amount: 9900,
      },
      {
        invoiceId: 1004,
        invoiceRefNumber: 'INV-VSMPS-20251023-004',
        purchaseOrderRefNumber: 'PO-VSMPS-20251022-1004',
        rfqRefNumber: 'RFQ-VSMPS-20251022-5004',
        quoteRefNumber: 'Q-VSMPS-20251022-3004',
        buyerName: 'Umbrella Inc',
        invoiceStatus: 'Unpaid',
        invoiceDate: '2025-10-23',
        dueDate: '2025-11-12',
        amount: 25450.75,
      },
      {
        invoiceId: 1005,
        invoiceRefNumber: 'INV-VSMPS-20251024-005',
        purchaseOrderRefNumber: 'PO-VSMPS-20251022-1005',
        rfqRefNumber: 'RFQ-VSMPS-20251022-5005',
        quoteRefNumber: 'Q-VSMPS-20251022-3005',
        buyerName: 'Wayne Enterprises',
        invoiceStatus: 'Paid',
        invoiceDate: '2025-10-24',
        dueDate: '2025-11-18',
        amount: 4321.99,
      },
    ];

    this.archivedDocumentData = [
      {
        invoiceId: 2001,
        invoiceRefNumber: 'INV-VSMPS-20240915-021',
        purchaseOrderRefNumber: 'PO-VSMPS-20251022-0901',
        rfqRefNumber: 'RFQ-VSMPS-20251022-4801',
        quoteRefNumber: 'Q-VSMPS-20251022-2801',
        buyerName: 'Stark Industries',
        invoiceStatus: 'Paid',
        invoiceDate: '2024-09-15',
        dueDate: '2024-10-15',
        amount: 15800,
      },
      {
        invoiceId: 2002,
        invoiceRefNumber: 'INV-VSMPS-20240810-017',
        purchaseOrderRefNumber: 'PO-VSMPS-20251022-0888',
        rfqRefNumber: 'RFQ-VSMPS-20251022-4705',
        quoteRefNumber: 'Q-VSMPS-20251022-2702',
        buyerName: 'Hooli',
        invoiceStatus: 'Overdue',
        invoiceDate: '2024-08-10',
        dueDate: '2024-09-09',
        amount: 7200,
      },
      {
        invoiceId: 2003,
        invoiceRefNumber: 'INV-VSMPS-20240701-010',
        purchaseOrderRefNumber: 'PO-VSMPS-20251022-0777',
        rfqRefNumber: 'RFQ-VSMPS-20251022-4603',
        quoteRefNumber: 'Q-VSMPS-20251022-2603',
        buyerName: 'Aperture Labs',
        invoiceStatus: 'Unpaid',
        invoiceDate: '2024-07-01',
        dueDate: '2024-07-31',
        amount: 5400.5,
      },
    ];

    this.totalRecords = this.documentData.length;
    this.archivedTotalRecords = this.archivedDocumentData.length;
  }

  // UI utilities
  getStatusColor(status: string): string {
    return this.statusColors[status] || '#000000';
  }

  // Status label renderer (status in UPPERCASE); shows days since paid when applicable
  getStatusLabel(row: any): string {
    console.log('row', row);
    const statusRaw = (row?.invoiceStatus || '').toString().trim();
    if (!statusRaw) return '-';
    const status = statusRaw.toLowerCase();

    if (status !== 'paid') {
      return statusRaw.toUpperCase();
    }

    const paymentDateRaw = row?.invoicePaymentDetails?.paymentDate || null;
    console.log('paymentDateRaw', paymentDateRaw);
    if (!paymentDateRaw) return 'PAID';
    const paid = new Date(paymentDateRaw);
    if (isNaN(paid.getTime())) return 'PAID';

    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOfPaid = new Date(paid.getFullYear(), paid.getMonth(), paid.getDate());
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const diff = Math.floor((startOfToday.getTime() - startOfPaid.getTime()) / MS_PER_DAY);
    if (diff <= 0) return 'PAID (TODAY)';
    const days = diff;
    const unit = days === 1 ? 'DAY' : 'DAYS';
    return `PAID (${days} ${unit})`;
  }

  setRowClass = (_row: any) => 'green-row-background';

  isDate(val: any): boolean {
    if (!val) return false;
    const d = new Date(val);
    return !isNaN(d.getTime());
  }

  // Filters/search
  onSearch(): void {
    const term = (this.searchParam || '').toString().trim();
    const hasDateFilter = !!this.fromDate || !!this.toDate;
    // Avoid API call when no search term AND no date filter
    if (!term && !hasDateFilter) {
      this.snack.open('Enter a search term or select dates', 'OK', { duration: 2500 });
      return;
    }
    this.searchParam = term;
    this.offset = 0;
    this.fetchInvoiceLists();
  }

  cancelFilter(): void {
    this.searchParam = '';
    this.fromDate = null;
    this.toDate = null;
    // Directly fetch reset list (bypass empty-search early return)
    this.offset = 0;
    this.fetchInvoiceLists();
  }

  onStatusSelect(status: string, index: number, label: string) {
    if (label === 'RFQ') {
      this.router.navigate(['/operations/supplier-service-rfq-list'], {
        queryParams: { selectedStatus: status, label },
      });
    } else if (label === 'Quote') {
      this.router.navigate(['/operations/service-supplier-quote-list'], {
        queryParams: { selectedStatus: status, label },
      });
    } else {
      // Invoice tab
      this.activeIndex = index;
      this.selectedStatus = status;
      this.offset = 0;
      this.fetchInvoiceLists();
    }
  }

  toggleDropdown() { this.dropdownOpen = !this.dropdownOpen; }

  toggleInvoiceTypeDropdown() {
    this.invoiceTypeDropdownOpen = !this.invoiceTypeDropdownOpen;
  }

  navigateToInvoiceType(type: string) {
    if (type === 'product') {
      this.router.navigate(['/operations/invoice-list']);
    }
    this.invoiceTypeDropdownOpen = false;
  }

  // Paging/sort
  onPageChange(event: any) {
    this.offset = event.offset;
    this.fetchInvoiceLists();
  }

  // Map selected status to API docStatus value
  private getDocStatusFilter(): string {
    const s = (this.selectedStatus || 'ALL').toString().trim().toLowerCase();
    if (s === 'paid' || s === 'unpaid' || s === 'overdue' || s == 'dispatched' || s=='delivered') {
      return s;
    }
    return 'ALL';
  }
  onArchivedPageChange(event: any) {
    this.archivedOffset = event.offset;
    this.fetchArchivedInvoiceLists();
  }
  onSort(event: any) {
    const sort = event.sorts?.[0];
    if (sort) {
      // Map UI column prop to backend sortField keys
      const map: Record<string, string> = {
        invoiceNumber: 'invoiceNumber',
        purchaseOrderNumber: 'purchaseOrderNumber',
        rfqRefNumber: 'rfqRefNumber',
        quoteRefNumber: 'quoteRefNumber',
        buyerName: 'buyerName',
        dueDate: 'dueDate',
      };
      const mapped = map[String(sort.prop)] || '';
      this.sortField = mapped;
      this.sortDirection = sort.dir;
      this.offset = 0; // reset to first page on sort change
      this.fetchInvoiceLists();
    }
  }
  onArchivedSort(event: any) {
    const sort = event.sorts?.[0];
    if (sort) {
      const map: Record<string, string> = {
        invoiceNumber: 'invoiceNumber',
        purchaseOrderNumber: 'purchaseOrderNumber',
        rfqRefNumber: 'rfqRefNumber',
        quoteRefNumber: 'quoteRefNumber',
        buyerName: 'buyerName',
        dueDate: 'dueDate',
      };
      this.archivedSortField = map[String(sort.prop)] || '';
      this.archivedSortDirection = sort.dir;
      this.archivedOffset = 0;
      this.fetchArchivedInvoiceLists();
    }
  }

  // Fetch archived invoices list
  async fetchArchivedInvoiceLists(): Promise<void> {
    try {
      this.showLoader = true;
      const supplierId = Number(this.cookieService.get('userId')) || this.userId || 0;
      const pageIndex = Number(this.archivedOffset) || 0;
      const pageSize = Number(this.archivedLimit) || 10;
      const recordOffset = pageIndex * pageSize;

      const formatToYMD = (d: any) => {
        if (!d) return null;
        const date = new Date(d);
        if (isNaN(date.getTime())) return null;
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };

      const reqBody: any = {
        supplierId,
        limit: pageSize,
        offset: recordOffset,
        searchTerm: '', // archived search not wired yet
        sortField: (this.archivedSortField || '').toString().trim(),
        sortDirection: (this.archivedSortDirection || '').toString().trim(),
        fromDate: formatToYMD(this.fromDate),
        toDate: formatToYMD(this.toDate),
      };

      const response: any = await this.invoiceListService.fetchArchivedSupplierServiceInvoiceLists(reqBody);

      const list = Array.isArray(response?.invoicesList) ? response.invoicesList : [];

      this.archivedDocumentData = list.map((inv: any) => {
        const due = inv?.dueDate ? new Date(inv.dueDate) : null;
        const today = new Date();
        const defaultStatus = due && !isNaN(due.getTime()) && due < new Date(today.getFullYear(), today.getMonth(), today.getDate())
          ? 'Overdue'
          : 'Unpaid';

        const payment = inv?.invoicePaymentDetails || null;
        const isPaid = Number(payment?.isPaid) === 1;
        const finalStatus = isPaid ? 'Paid' : (inv?.invoiceStatus ?? defaultStatus);

        return {
          invoiceId: inv?.invoiceId ?? null,
          invoiceRefNumber: inv?.invoiceNumber ?? '-',
          purchaseOrderId: inv?.purchaseOrderId ?? inv?.poId ?? null,
          purchaseOrderRefNumber: inv?.purchaseOrderRefNumber ?? '-',
          rfqId: inv?.rfqId ?? null,
          rfqRefNumber: inv?.rfqRefNumber ?? '-',
          quoteId: inv?.quoteId ?? inv?.quotationId ?? null,
          quoteRefNumber: inv?.quoteRefNumber ?? '-',
          buyerName: inv?.buyerCompanyName ?? '-',
          buyerId: inv?.buyerId ?? null,
          amount: inv?.amount ?? null,
          invoiceDate: inv?.invoiceDate ?? null,
          dueDate: inv?.dueDate ?? null,
          invoiceStatus: finalStatus,
          currency: inv?.currency ?? null,
          invoicePaymentDetails: payment,
          isDispatched: inv?.isDispatched ?? false,
          dispatchId: inv?.dispatchId ?? null,
          dispatchStatus : inv?.isDispatched ? "Dispatched" : "Pending",
          isDelivered: inv?.isDelivered ?? false,
          receiptId: inv?.receiptId ?? null,
          deliveryStatus : inv?.isDelivered ? "Delivered" : "Pending",
          companyEmailId: inv?.companyEmailId ?? null,
          buyerCompanyPhoneNumber: inv?.buyerCompanyPhoneNumber ?? null,
          buyerCompanyFax: inv?.buyerCompanyFax ?? null,
          buyerCompanyAddress: inv?.buyerCompanyAddress ?? null,
          buyerContact: inv?.buyerContact ?? null,
          buyerCompanyName: inv?.buyerCompanyName ?? null,
          poId: inv?.poId ?? null,
          supplierId:inv?.supplierId ?? null,
          supplierDispatchRefNo:inv?.supplierDispatchRefNo ?? null,

        } as any;
      });

      const total = Number(response?.countData?.totalCount ?? 0);
      this.archivedTotalRecords = isNaN(total) ? this.archivedDocumentData.length : total;
    } catch (error) {
      console.error('Failed to fetch archived invoices list', error);
      this.snack.open('Failed to fetch archived invoices list', 'OK', { duration: 4000 });
    } finally {
      this.showLoader = false;
    }
  }

  // Column pickers
  toggleSelection(item: any) {
    item.selected = !item.selected;
    this.updateSelectedColumns();
  }
  updateSelectedColumns() {
    this.selectedColumns = this.items.filter(i => i.selected).map(i => ({ label: i.label, prop: i.prop }));
  }
  archivedToggleSelection(item: any) {
    item.selected = !item.selected;
    this.updateArchivedSelectedColumns();
  }
  updateArchivedSelectedColumns() {
    this.archivedSelectedColumns = this.archivedItems.filter(i => i.selected).map(i => ({ label: i.label, prop: i.prop }));
  }

  // Selection handlers
  toggleSelectAll(event: any, section: 'active' | 'archived') {
    const checked = !!event?.target?.checked;
    if (section === 'active') {
      this.selectedInvoices = checked ? this.documentData.map(r => r.invoiceId) : [];
    } else {
      this.selectedArchivedInvoices = checked ? this.archivedDocumentData.map(r => r.invoiceId) : [];
    }
  }
  selectDeselectInvoice(event: any, invoiceId: number, section: 'active' | 'archived') {
    const checked = !!event?.target?.checked;
    if (section === 'active') {
      this.selectedInvoices = checked
        ? Array.from(new Set([...this.selectedInvoices, invoiceId]))
        : this.selectedInvoices.filter(id => id !== invoiceId);
    } else {
      this.selectedArchivedInvoices = checked
        ? Array.from(new Set([...this.selectedArchivedInvoices, invoiceId]))
        : this.selectedArchivedInvoices.filter(id => id !== invoiceId);
    }
  }
  isChecked(invoiceId: number, section: 'active' | 'archived') {
    return section === 'active'
      ? this.selectedInvoices.includes(invoiceId)
      : this.selectedArchivedInvoices.includes(invoiceId);
  }

  // Archive section
  toggleArchived() {
    this.isArchivedExpand = !this.isArchivedExpand;
    if (this.isArchivedExpand && this.archivedDocumentData.length === 0) {
      this.archivedOffset = 0;
      this.fetchArchivedInvoiceLists();
    }
  }

  invoiceOperation(operation: 'archive' | 'unarchive') {
    const supplierId = Number(this.cookieService.get('userId')) || this.userId || 0;
    const ids = operation === 'archive' ? this.selectedInvoices : this.selectedArchivedInvoices;

    if (!ids || ids.length === 0) {
      this.snack.open('Please select at least one invoice', 'OK', { duration: 3000 });
      return;
    }

    const reqBody = {
      supplierId,
      invoiceIds: ids,
      operationType: operation,
    } as any;

    this.showLoader = true;
    this.invoiceListService
      .supplierServiceInvoiceListsOperations(reqBody)
      .then(async (res: any) => {
        const processed = Array.isArray(res?.processed) ? res.processed.length : 0;
        const failed = Array.isArray(res?.failed) ? res.failed.length : 0;
        if (failed === 0) {
          this.snack.open(`${operation === 'archive' ? 'Archived' : 'Unarchived'} ${processed} invoice(s)`, 'OK', { duration: 3000 });
        } else {
          this.snack.open(`${operation === 'archive' ? 'Archived' : 'Unarchived'} ${processed}, failed ${failed}`, 'OK', { duration: 4000 });
        }

        // Clear selection and refresh both lists to reflect movement
        this.selectedInvoices = [];
        this.selectedArchivedInvoices = [];
        this.offset = 0;
        this.archivedOffset = 0;
        await this.fetchInvoiceLists();
        if (this.isArchivedExpand) {
          await this.fetchArchivedInvoiceLists();
        }
      })
      .catch((err: any) => {
        console.error('Invoice operation failed', err);
        this.snack.open('Operation failed. Please try again.', 'OK', { duration: 4000 });
      })
      .finally(() => {
        this.showLoader = false;
      });
  }

  async RestoreInvoice(_confirm: boolean, _row: any) {
    // Stub: show a toast to confirm the UI flow
    this.snack.open('Invoice restored (UI only)', 'OK', { duration: 2000 });
  }

  openInvoice(_row: any) {
    // Stub for preview navigation
    this.snack.open('Preview invoice (UI only)', 'OK', { duration: 1500 });
  }

  // Action column handlers
  onViewClick(row: any) {
    const qp: any = {};
    if (row?.purchaseOrderId != null) qp.poId = row.purchaseOrderId;
    if (row?.rfqId != null) qp.rfqId = row.rfqId;
    if (row?.quoteId != null) qp.quoteId = row.quoteId;
    if (row?.buyerId != null) qp.buyerId = row.buyerId;
    qp.poStatusValue='Approved';

    this.router.navigate(['/operations/create-service-invoice'], {
      queryParams: qp,
    });
  }

  onChatClick(_row: any) {
    this.snack.open('Chat button clicked', 'OK', { duration: 2000 });
  }

  // Display helpers
  getDisplayAmount(row: any): string {
    const val = row?.totalAmount ?? row?.amount ?? row?.invoiceAmount ?? row?.totalCost ?? null;
    if (val === null || val === undefined || val === '') return '-';
    const code = (row?.currency?.currencyCode || row?.currency || '').toString().trim();
    // console.log("currencyCode",this.currencyCode,code);
    this.currencyCode = code;
    try {
      const n = Number(val);
      const amt = isNaN(n)
        ? String(val)
        : n.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
      return code ? `${code} ${amt}` : amt;
    } catch {
      return code ? `${code} ${String(val)}` : String(val);
    }
  }

  // Formats due date with remaining/passed days based on invoiceStatus
  formatDueDate(row: any): string {
    const raw = row?.dueDate;
    if (!raw) return '-';
    const due = new Date(raw);
    if (isNaN(due.getTime())) return '-';

    const format = (d: Date) => {
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    };

    const status = (row?.invoiceStatus || '').toString();
    // If paid, only show the date as requested
    if (status.toLowerCase() === 'paid') {
      return format(due);
    }

    // Compute day diff using local date (ignore time-of-day)
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOfDue = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const diff = Math.floor((startOfDue.getTime() - startOfToday.getTime()) / MS_PER_DAY);

    if (diff >= 0) {
      // Unpaid/upcoming
      if (diff === 0) {
        return `${format(due)} (Today)`;
      }
      const days = diff;
      const unit = days === 1 ? 'day' : 'days';
      return `${format(due)} (${days} ${unit} remaining)`;
    } else {
      // Overdue
      const days = Math.abs(diff);
      const unit = days === 1 ? 'day' : 'days';
      return `${format(due)} (${days} ${unit} passed)`;
    }
  }

  exportAllInvoices() {
    this.fullLoaderService.showLoader('Exporting Invoices...');
    const supplierId = Number(this.cookieService.get('userId')) || this.userId || 0;
    const reqBody: any = { supplierId };

    this.invoiceListService
      .exportAllSupplierServiceInvoiceList(reqBody)
      .then((response: any) => {
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.invoicesList)
          ? response.invoicesList
          : [];

        // Map API rows into UI-like rows to reuse formatters
        const rows = list.map((inv: any) => {
          const due = inv?.dueDate ? new Date(inv.dueDate) : null;
          const today = new Date();
          const defaultStatus = due && !isNaN(due.getTime()) && due < new Date(today.getFullYear(), today.getMonth(), today.getDate())
            ? 'Overdue'
            : 'Unpaid';
          const payment = inv?.invoicePaymentDetails || null;
          const isPaid = Number(payment?.isPaid) === 1;
          const finalStatus = isPaid ? 'Paid' : (inv?.invoiceStatus ?? defaultStatus);

          return {
            invoiceId: inv?.invoiceId ?? null,
            invoiceRefNumber: inv?.invoiceNumber ?? '-',
            purchaseOrderRefNumber: inv?.purchaseOrderRefNumber ?? '-',
            rfqRefNumber: inv?.rfqRefNumber ?? '-',
            quoteRefNumber: inv?.quoteRefNumber ?? '-',
            buyerName: inv?.buyerCompanyName ?? '-',
            supplierCompanyName: inv?.supplierCompanyName ?? '-',
            supplierName: inv?.supplierName ?? '-',
            amount: inv?.amount ?? null,
            invoiceDate: inv?.invoiceDate ?? null,
            dueDate: inv?.dueDate ?? null,
            invoiceStatus: finalStatus,
            currency: inv?.currency ?? null,
            invoicePaymentDetails: payment,
            isDispatched: inv?.isDispatched ?? false,
            dispatchId: inv?.dispatchId ?? null,
            dispatchStatus : inv?.isDispatched ? "Dispatched" : "Pending",
            isDelivered: inv?.isDelivered ?? false,
            receiptId: inv?.receiptId ?? null,
            deliveryStatus : inv?.isDelivered ? "Delivered" : "Pending",
            companyEmailId: inv?.companyEmailId ?? null,
            buyerCompanyPhoneNumber: inv?.buyerCompanyPhoneNumber ?? null,
            buyerCompanyFax: inv?.buyerCompanyFax ?? null,
            buyerCompanyAddress: inv?.buyerCompanyAddress ?? null,
            buyerContact: inv?.buyerContact ?? null,
            buyerCompanyName: inv?.buyerCompanyName ?? null,
            poId: inv?.poId ?? null,
            supplierId:inv?.supplierId ?? null,
          } as any;
        });

        // Build export rows with same columns as table (excluding checkbox and action)
        const exportData: any[] = rows.map((row: any, index: number) => ({
          'Sr. No.': index + 1,
          'Invoice Number': row.invoiceRefNumber || '',
          'Order Reference': row.purchaseOrderRefNumber || '',
          'RFQ Reference': row.rfqRefNumber || '',
          'Quote Reference': row.quoteRefNumber || '',
          'Buyer': row.buyerName || '',
          'Amount': this.getDisplayAmount(row),
          'DueDate': this.formatDueDate(row),
          'Status': this.getStatusLabel(row),
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Invoices');

        const today = new Date();
        const formatted = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
        XLSX.writeFile(wb, `Supplier_Service_Invoices_List_${formatted}.xlsx`);

        this.snack.open('Invoices data exported', 'OK', { duration: 4000 });
      })
      .catch((e: any) => {
        console.error('Export error', e);
        this.snack.open('Failed to export invoices', 'OK', { duration: 4000 });
      })
      .finally(() => {
        this.fullLoaderService.hideLoader();
      });
  }

  // Chat toggles
  closeChat() { this.isChatOpen = false; }

  // Modal controls
  openMarkInvoicePaidModal(row?: any) {
    this.modalInvoiceContext = row || null;

    // Reset form and patch display values from the selected row
    if (this.markInvoiceAsPaidForm) {
      this.markInvoiceAsPaidForm.reset();
      const invoiceRefNumber = row?.invoiceRefNumber || '-';
      const supplierCompanyName = row?.supplierCompanyName || row?.supplierName || '-';
      const amountDue = this.getDisplayAmount(row);
      this.markInvoiceAsPaidForm.patchValue({
        invoiceRefNumber,
        supplierCompanyName,
        amountDue,
        amountPaid: row?.amount ?? null,
      });

      // Prefill with payment details when available (view-only)
      const ipd = row?.invoicePaymentDetails || null;
      if (ipd && Number(ipd.isPaid) === 1) {
        const normalizeDate = (d: any) => {
          const date = new Date(d);
          if (isNaN(date.getTime())) return null;
          const yyyy = date.getFullYear();
          const mm = String(date.getMonth() + 1).padStart(2, '0');
          const dd = String(date.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        };
        this.markInvoiceAsPaidForm.patchValue({
          paymentDate: normalizeDate(ipd.paymentDate),
          amountPaid: ipd.amountPaid ?? row?.amount ?? null,
          paymentMethod: (ipd.paymentMethod || ''),
          transactionRefNumber: (ipd.transactionReferenceNumber || ''),
          fromAccount: (ipd.fromAccount || ''),
          toAccount: (ipd.toAccount || ''),
          notes: (ipd.additionalNotes || ''),
        });
      }
    }

    this.isMarkInvoiceAsPaidSaving = false;
    this.isMarkInvoicePaidModalOpen = true;
  }

  closeMarkInvoicePaidModal() {
    this.isMarkInvoicePaidModalOpen = false;
  }

  // Save button is not needed for supplier view (read-only modal)
  get showSaveButton(): boolean { return false; }

  // Basic guard during save to avoid double clicks (UI only)
  isMarkInvoiceAsPaidSaving = false;

  onProofSelected(evt: Event) {
    const input = evt.target as HTMLInputElement;
    const file = input?.files && input.files.length > 0 ? input.files[0] : null;
    this.markInvoiceAsPaidForm.get('paymentProof')?.setValue(file);
  }

  onSaveMarkInvoiceAsPaid() {
    if (this.isMarkInvoiceAsPaidSaving) return;
    this.isMarkInvoiceAsPaidSaving = true;

    // For now, we only show a snackbar message per requirements
    const invoiceNumber = this.modalInvoiceContext?.invoiceRefNumber
      || this.markInvoiceAsPaidForm.get('invoiceRefNumber')?.value
      || '-';

    this.snack.open(`Invoice with number - ${invoiceNumber} is marked as paid`, 'OK', { duration: 3500 });

    // Close the modal after showing toast
    this.closeMarkInvoicePaidModal();
    this.isMarkInvoiceAsPaidSaving = false;
  }


  createDispatch(invoiceId:number,data:any){
    if(data.isDispatched && data.dispatchId!=null){
      this.router.navigate(['/operations', 'create-dispatch'], {
        queryParams: { invoiceId: invoiceId, dispatchId: data.dispatchId },
      });
    }
    else{
      this.router.navigate(['/operations', 'create-dispatch'], {
        queryParams: { invoiceId: invoiceId, dispatchId: 'new' },
      });
    }

  }

  confirmOrderReceipt(dispatchId:number,data:any){

    if(!dispatchId && data.dispatchStatus=='pending'){
      this.snack.open(`Order is not dispatched yet`, 'OK', { duration: 3500 });
    }
    else if(data.dispatchStatus =='Dispatched' && !data.isDelivered){
      this.snack.open(`Order is not delivered yet`, 'OK', { duration: 3500 });
    }
    else if(data.dispatchStatus =='Dispatched' && data.isDelivered && data.receiptId){
      this.router.navigate(['/operations', 'order-receipt-details'], {
        queryParams: { dispatchId: dispatchId, receiptId: data.receiptId },
      });
    }
    else{
      this.snack.open(`Action not permitted, Order is not dispatched/delivered yet`, 'OK', { duration: 3500 });
    }


}

toggleContactPopUp(value: boolean, row?: any){
  this.showContactPopup = value;

  if(value){
    if(row){

      this.selectedBuyer = {
        companyName: row.buyerCompanyName,
        contactPerson: row.buyerContact,
        email: row.companyEmailId,
        phone: row.buyerCompanyPhoneNumber,
        address: row.buyerCompanyAddress,
        fax: row.buyerCompanyFax

      };

    }

  }
}

async generateInvoicePdf(row:any, docType:string) {
  this.fullLoaderService.showLoader();
  try {


    const poId = Number(row.poId);
    const rfqId = Number(row.rfqId );
    const quoteId = Number(row.quoteId );
    const buyerId = Number(row.buyerId );
    const userId = Number(row.supplierId);

    const name = Math.random().toString(36).substring(2, 10);

    const fileName = `DOC-${name}.pdf`;

    // Basic validation/logging of inputs
    console.log('createAndFetchInvoiceDetails -> inputs:', {
      row,
      poId,
      userId,
      rfqId,
      quoteId,
      buyerId,
    });

    const pdf = await this.settingsService.generateServiceInvoicePdf({
      servicePurchaseOrderId: poId,
      userId,
      serviceRfqId: rfqId,
      serviceQuotationId: quoteId,
      buyerId,
      docType
    });





    const uint8Array = new Uint8Array(pdf.data);
    const blob = new Blob([uint8Array], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);


    // Create a temporary <a> element
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Determine if invoice is already sent based on API response
    // and control editability + invoice number generation accordingly

  } catch (error) {
    console.error('createAndGetInvoiceDetails -> error:', error);
    this.snack.open('Failed to fetch invoice details', 'Dismiss', {
      duration: 3000,
    });
    throw error;
  } finally {
    this.fullLoaderService.hideLoader();
  }
}

async generateInvoiceExcel(row:any, docType:string) {
  this.fullLoaderService.showLoader();
  try {


    const poId = Number(row.poId);
    const rfqId = Number(row.rfqId );
    const quoteId = Number(row.quoteId );
    const buyerId = Number(row.buyerId );
    const userId = Number(row.supplierId);

    const name = Math.random().toString(36).substring(2, 10);

    const fileName = `DOC-${docType}-${name}.xlsx`;

    // Basic validation/logging of inputs
    console.log('createAndFetchInvoiceDetails -> inputs:', {
      row,
      poId,
      userId,
      rfqId,
      quoteId,
      buyerId,
    });

    const result = await this.settingsService.fetchServiceInvoiceAttachmentExcelData({
      servicePurchaseOrderId: poId,
      userId,
      serviceRfqId: rfqId,
      serviceQuotationId: quoteId,
      buyerId,
      docType
    });

    console.log(result,'result')



    const data = {
      invoiceDetails: result.invoiceDetails,
      buyerDetails: {
        name: result.buyerCompanyDetails.buyerName,
        address: result.buyerCompanyDetails.buyerAddress,
        contact: result.buyerDetails.buyerContactPerson,
        phone: result.buyerCompanyDetails.buyerPhone,
        email: result.buyerCompanyDetails.buyerEmail,
        billingAddress: result.buyerCompanyDetails.billingAddress,

      },
      supplierDetails: {
        name: result.supplierCompanyDetails.supplierContactName,
        address: result.supplierCompanyDetails.supplierAddress,
        contact: result.supplierDetails.supplierContactName,
        phone: result.supplierCompanyDetails.supplierPhone,
        email: result.supplierCompanyDetails.supplierEmail,
        billingAddress: result.supplierCompanyDetails.supplierAddress,
        vat: result.supplierCompanyDetails.supplierTaxNumber,

      },
      bankDetails: result.bankDetails,

      freightForwarderDetails: {
        PIC: result.freightForwarderDetails.freightPIC,
        phone: result.freightForwarderDetails.freightPhone,
        fax: result.freightForwarderDetails.freightFax,
        email: result.freightForwarderDetails.freightEmail,
        address: result.freightForwarderDetails.freightAddress,
      },
      portAgentDetails: {
        name: result.freightForwarderDetails.portAgencyName,
        phone:result.freightForwarderDetails.portAgentPhoneNumber,
        fax: result.freightForwarderDetails.portAgentPhoneFax,
        email: result.freightForwarderDetails.portAgentEmail,
        contact: result.freightForwarderDetails.portAgentContact,
      },
      costSummary:{
        lineItemSubtotal: result.priceDetails.subtotal || 0,
        discountAmount: result.priceDetails.generalDiscount?.type === 'percentage'
          ? ((result.priceDetails.subtotal || 0) * (parseFloat(result.priceDetails.generalDiscount?.value) || 0) / 100)
          : (parseFloat(result.priceDetails.generalDiscount?.value) || 0),
        taxAmount: result.priceDetails.taxAmount || 0,
        otherCharges: result.priceDetails.freight,
        totalAdditionalCost: result.priceDetails.freightTotal,
        currency: result.priceDetails.currency.currencyCode,
        grandTotal:  result.priceDetails.total
      },

      vesselDetails: {
        vesselName: result.priceDetails.vesselName,
        vesselIMONumber: result.priceDetails.vesselIMONumber,
        vesselType: result.priceDetails.vesselType,
        vesselClass: result.priceDetails.vesselClass,
      },
      portDetails: result.portDetails,
      priceDetails: result.priceDetails,
      paymentTerms: result.paymentTerms,
      packagingDetails: result.packagingDetails || {},


      lineItems: result.lineItems.map((item: {
        supplierPartNumber: any;
        requestedQuantity: any;
        supplierUom: any;
        discount: any;
        discountedUnitPrice: any;
        partNo: any;
        supplierPartNo: any;
        description: any;
        supplierQuantity: any;
        unitOfMeasure: any;
        supplierUOM: any;
        unitCost: any;
        discountPercent: any;
        totalCost: any;
        leadTime: any;
        supplierComment: any; partNumber: any; itemDescription: any; quantity: any; uom: any; buyerComments: any;
  }, index: number) => ({
        srNo: index + 1,
        partNumber: item.partNumber,
        supplierPartNumber: item.supplierPartNumber,
        itemDescription: item.itemDescription,
        requestedQty: item.requestedQuantity,
        quotedQuantity: item.supplierQuantity,
        requestedUom: item.uom,
        quotedUom: item.supplierUom,
        unitCost: item.unitCost,
        discount: item.discount,
        discountedUnitCost: item.discountedUnitPrice,
        totalCost: item.totalCost,

      }))
    };

    console.log(data,'data')


    if(docType == 'Commercial Invoice'){
      this.generateCommercialInvoice(data,fileName)
    }
    else if(docType == 'Delivery Note'){
      this.generateDeliveryNote(data,fileName)
    }
    else if(docType == 'Packaging List'){
      this.generatePackingList(data,fileName)
    }




  } catch (error) {
    console.error('createAndGetInvoiceDetails -> error:', error);
    this.snack.open('Failed to fetch invoice details', 'Dismiss', {
      duration: 3000,
    });
    throw error;
  } finally {
    this.fullLoaderService.hideLoader();
  }
}

async generateCommercialInvoice(data:any,fileName:string){

  const workbook = new Excel.Workbook();
  const ws = workbook.addWorksheet('Commercial Invoice');

  // === TITLE ===
  ws.mergeCells('A1:G1');
  const title = ws.getCell('A1');
  title.value = 'Commercial Invoice';
  title.font = { size: 18, bold: true, color: { argb: '1F4E78' } };
  title.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.addRow([]);

  const invoiceHeader = ws.addRow(['Invoice Details']);
  invoiceHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'f9bccb' } };
  invoiceHeader.getCell(1).font = { bold: true };
  ws.addRow([]);
  ws.addRow(['Invoice Number', data.invoiceDetails.invoiceNumber]);
  ws.addRow(['Invoice Date', data.invoiceDetails.invoiceDate]);
  ws.addRow(['Due Date', data.invoiceDetails.dueDate]);
  ws.addRow(['Purchase Order Ref', data.invoiceDetails.purchaseOrderNumber]);
  ws.addRow(['Quotation Ref', data.invoiceDetails.quoteRefNumber]);
  ws.addRow([]);
  ws.addRow([]);


  // === BUYER AND SUPPLIER DETAILS ===
  const BsHeader = ws.addRow(['Buyer Details (Bill To)', '', '', 'Supplier Details (Bill From)']);
  BsHeader.eachCell(c => {
    c.font = { bold: true };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2CC' } };
  });

  const buyerEntries = Object.entries(data.buyerDetails).map(([key, value]) => [
    labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()),
    value
  ]);

  const supplierEntries = Object.entries(data.supplierDetails).map(([key, value]) => [
    labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()),
    value
  ]);

  // Pad so both arrays have the same length
  const maxRows = Math.max(buyerEntries.length, supplierEntries.length);
  const paddedBuyer = [...buyerEntries, ...Array(maxRows - buyerEntries.length).fill(['', ''])];
  const paddedSupplier = [...supplierEntries, ...Array(maxRows - supplierEntries.length).fill(['', ''])];


  for (let i = 0; i < maxRows; i++) {
    const row = ws.addRow([
      paddedBuyer[i][0], paddedBuyer[i][1],
      '',
      paddedSupplier[i][0], paddedSupplier[i][1]
    ]);


    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', wrapText: true };
    });
  }


  ws.addRow([]);



  // === LINE ITEMS ===
  const liHeader = ws.addRow(['Line Items']);
  liHeader.getCell(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'BDD7EE' },
  };
  liHeader.getCell(1).font = { bold: true };
  ws.addRow([]);

  const tableHeader = ws.addRow([
    'Sr. No',
    'Part Number',
    'Item Description',
    'Requested Qty',
    'Requested UOM',
    'Unit Cost',
    'Discount',
    'Discounted Unit Cost',
    'Total Cost'
  ]);
  tableHeader.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'D9D9D9' },
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Add Line Items
  data.lineItems.forEach((item: {
    discountedUnitCost: any;
    supplierRemarks: any; srNo: any; partNumber: any; supplierPartNumber: any; itemDescription: any; requestedQty: any; quotedQuantity: any; requestedUom: any; quotedUom: any; unitCost: any; discount: any; totalCost: any; leadDays: any; supplierComment: any;
}) => {
    const row = ws.addRow([
      item.srNo,
      item.partNumber,
      item.itemDescription,
      item.requestedQty,
      item.requestedUom,
      item.unitCost,
      item.discount,
      item.discountedUnitCost,
      item.totalCost

    ]);
    row.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
      cell.alignment = { wrapText: true, vertical: 'middle' };
    });
  });

  ws.addRow([]);



  const costSummaryHeader = ws.addRow(['Cost Summary']);
  costSummaryHeader.getCell(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F9F9F9' },
  };
  costSummaryHeader.getCell(1).font = { bold: true };
  ws.addRow([]);

  // Cost Summary Table
  const costSummaryTableHeader = ws.addRow(['Description', 'Amount']);
  costSummaryTableHeader.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'D9D9D9' },
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Line Total Amount
  const lineTotalRow = ws.addRow([
    'Line Total Amount',
    data.costSummary.lineItemSubtotal,
  ]);
  lineTotalRow.eachCell((cell) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // General Discount Amount
  const discountRow = ws.addRow([
    'General Discount Amount',
    data.costSummary.discountAmount,
  ]);
  discountRow.eachCell((cell) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Adding Additional Charges
  let totalAdditionalCharges = 0;
  data.costSummary.otherCharges.forEach((charge: { description: any; value: number; }) => {
    const row = ws.addRow([charge.description, charge.value]);
    row.eachCell((cell) => {
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });
    totalAdditionalCharges += charge.value;
  });

  // Total Additional Cost
  const totalAdditionalCostRow = ws.addRow([
    'Total Additional Cost',
    data.costSummary.totalAdditionalCost,
  ]);
  totalAdditionalCostRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Tax Amount
  const taxRow = ws.addRow([
    'Tax Amount',
    data.costSummary.taxAmount,
  ]);
  taxRow.eachCell((cell) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Grand Total
  const grandTotalRow = ws.addRow([
    'Grand Total',
    `${data.costSummary.grandTotal} ${data.costSummary.currency}`,
  ]);
  grandTotalRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  ws.addRow([]);


  const shipHeader = ws.addRow(['Shipment Details']);
  shipHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'f9bccb' } };
  shipHeader.getCell(1).font = { bold: true };
  ws.addRow([]);
  ws.addRow(['Incoterms', data.portDetails.incoterms]);
  ws.addRow(['Port', data.portDetails.portName]);
  ws.addRow(['Expected Delivery Date', data.portDetails.requestedDeliveryDate]);
  ws.addRow(['Vessel Name', data.vesselDetails.vesselName]);
  ws.addRow([]);
  ws.addRow([]);

  const payHeader = ws.addRow(['Payment Details']);
  payHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'f9bccb' } };
  payHeader.getCell(1).font = { bold: true };
  ws.addRow([]);
  ws.addRow(['Payment Terms', data.priceDetails.vesselName]);
  ws.addRow(['Currency', data.priceDetails.currency.currencyCode]);
  ws.addRow(['Total Amount Due', data.priceDetails.total]);
  ws.addRow(['Due Date', data.invoiceDetails.dueDate]);
  ws.addRow([]);
  ws.addRow([]);

  const bankHeader = ws.addRow(['Bank Details']);
  bankHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'f9bccb' } };
  bankHeader.getCell(1).font = { bold: true };
  ws.addRow([]);
  ws.addRow(['Account Holder', data.bankDetails.bankAccountHolderName]);
  ws.addRow(['Bank Name', data.bankDetails.bankName]);
  ws.addRow(['Account Number', data.bankDetails.accountNumberIBAN]);
  ws.addRow(['SWIFT/BIC Code', data.bankDetails.swiftBicCode]);
  ws.addRow(['Bank Address', data.bankDetails.bankAddress]);
  ws.addRow([]);
  ws.addRow([]);

  const authHeader = ws.addRow(['Authorized Signature']);
  authHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FCE4D6' } };
  authHeader.getCell(1).font = { bold: true };
  ws.addRow([]);
  ws.addRow(['Name']);
  ws.addRow(['Account Holder']);
  ws.addRow(['Signature']);
  ws.addRow([]);
  ws.addRow([]);

  // // === VSMPS Buyer and Supplier section ===
  // ws.addRow([`VSMPS Supplier:`]);
  // ws.addRow([data.supplierDetails.name]);
  // ws.addRow([data.supplierDetails.address]);

  // ws.addRow([`VSMPS Buyer:`]);
  // ws.addRow([data.buyerDetails.name]);
  // ws.addRow([data.buyerDetails.address]);

  // ws.addRow([]);

  ws.addRow([]);
  ws.addRow(['Copyright © Varuna Sentinels BV. All rights reserved.']);

  // === AUTO WIDTH ===
  const colCount = ws.actualColumnCount;
  for (let i = 1; i <= colCount; i++) {
    let maxLen = 12;
    ws.eachRow({ includeEmpty: true }, (r) => {
      const val = r.getCell(i).value;
      const len = val ? val.toString().length : 0;
      if (len > maxLen) maxLen = len;
    });
    ws.getColumn(i).width = Math.min(maxLen + 4, 40);
  }

  workbook.xlsx.writeBuffer().then(buffer => {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    FileSaver.saveAs(blob, fileName);
  });


 }

 async generateDeliveryNote(data:any,fileName:string){

  const workbook = new Excel.Workbook();
  const ws = workbook.addWorksheet('Delivery Note');

  // === TITLE ===
  ws.mergeCells('A1:G1');
  const title = ws.getCell('A1');
  title.value = 'Delivery Note';
  title.font = { size: 18, bold: true, color: { argb: '1F4E78' } };
  title.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.addRow([]);



  // === BUYER AND SUPPLIER DETAILS ===
  const BsHeader = ws.addRow(['Shipper Details (Supplier)', '', '', 'Consignee Details (Buyer)']);
  BsHeader.eachCell(c => {
    c.font = { bold: true };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2CC' } };
  });
  ws.addRow([]);

  const buyerEntries = Object.entries(data.buyerDetails).map(([key, value]) => [
    labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()),
    value
  ]);

  const supplierEntries = Object.entries(data.supplierDetails).map(([key, value]) => [
    labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()),
    value
  ]);

  // Pad so both arrays have the same length
  const maxRows = Math.max(buyerEntries.length, supplierEntries.length);
  const paddedBuyer = [...buyerEntries, ...Array(maxRows - buyerEntries.length).fill(['', ''])];
  const paddedSupplier = [...supplierEntries, ...Array(maxRows - supplierEntries.length).fill(['', ''])];


  for (let i = 0; i < maxRows; i++) {
    const row = ws.addRow([
      paddedBuyer[i][0], paddedBuyer[i][1],
      '',
      paddedSupplier[i][0], paddedSupplier[i][1]
    ]);


    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', wrapText: true };
    });
  }




  ws.addRow([]);
  ws.addRow([]);

     // === QUOTATION DETAILS ===
const shipmentHeader = ws.addRow(['Shipment Information']);
shipmentHeader.getCell(1).fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'D9E1F2' },
};
shipmentHeader.getCell(1).font = { bold: true };
ws.addRow([]);

// Quotation Info
const poInfo = [
  [
    'Vessel Name:',
    data.vesselDetails.vesselName,
    '',
    'Packing List Date:',
    data.invoiceDetails.invoiceDate,
  ],
  [
    'Port:',
    data.portDetails.portName,
    '',

    'Purchase Order Ref:',
    data.invoiceDetails.purchaseOrderNumber,
  ],
  [
    'Expected Delivery Date:',
    data.portDetails.requestedDeliveryDate,
    '',

    'Commercial Invoice Ref:',
    data.invoiceDetails.invoiceNumber,
  ],
  [
    'Freight Forwarder Company Name:',
    data.freightForwarderDetails.name,
    '',
    'Port Agent Company Name:',
    data.portAgentDetails.name,
  ],
  [
    'Freight Forwarder Address:',
    data.freightForwarderDetails.address,
    '',
    'Port Agent Address:',
    data.portAgentDetails.address,
  ],
  [
    'Incoterms:',
    data.portDetails.incoterms,
    '',
    '',
    '',
  ]
];
poInfo.forEach((r) => {
  const row = ws.addRow(r);
  row.eachCell((c) => (c.alignment = { vertical: 'middle' }));
});
ws.addRow([]);
ws.addRow([]);



  // === LINE ITEMS ===
  const liHeader = ws.addRow(['Line Items']);
  liHeader.getCell(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'BDD7EE' },
  };
  liHeader.getCell(1).font = { bold: true };
  ws.addRow([]);

  const tableHeader = ws.addRow([
    'Sr. No',
    'Part Number',
    'Item Description',
    'Requested Qty',
    'Requested UOM',
    'Unit Cost',
    'Discount',
    'Discounted Unit Cost',
    'Total Cost'
  ]);
  tableHeader.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'D9D9D9' },
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Add Line Items
  data.lineItems.forEach((item: {
    discountedUnitCost: any;
    supplierRemarks: any; srNo: any; partNumber: any; supplierPartNumber: any; itemDescription: any; requestedQty: any; quotedQuantity: any; requestedUom: any; quotedUom: any; unitCost: any; discount: any; totalCost: any; leadDays: any; supplierComment: any;
}) => {
    const row = ws.addRow([
      item.srNo,
      item.partNumber,
      item.itemDescription,
      item.requestedQty,
      item.requestedUom,
      item.unitCost,
      item.discount,
      item.discountedUnitCost,
      item.totalCost

    ]);
    row.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
      cell.alignment = { wrapText: true, vertical: 'middle' };
    });
  });

  ws.addRow([]);



  const costSummaryHeader = ws.addRow(['Cost Summary']);
  costSummaryHeader.getCell(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F9F9F9' },
  };
  costSummaryHeader.getCell(1).font = { bold: true };
  ws.addRow([]);

  // Cost Summary Table
  const costSummaryTableHeader = ws.addRow(['Description', 'Amount']);
  costSummaryTableHeader.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'D9D9D9' },
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Line Total Amount
  const lineTotalRow = ws.addRow([
    'Line Total Amount',
    data.costSummary.lineItemSubtotal,
  ]);
  lineTotalRow.eachCell((cell) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // General Discount Amount
  const discountRow = ws.addRow([
    'General Discount Amount',
    data.costSummary.discountAmount,
  ]);
  discountRow.eachCell((cell) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Adding Additional Charges
  let totalAdditionalCharges = 0;
  data.costSummary.otherCharges.forEach((charge: { description: any; value: number; }) => {
    const row = ws.addRow([charge.description, charge.value]);
    row.eachCell((cell) => {
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });
    totalAdditionalCharges += charge.value;
  });

  // Total Additional Cost
  const totalAdditionalCostRow = ws.addRow([
    'Total Additional Cost',
    data.costSummary.totalAdditionalCost,
  ]);
  totalAdditionalCostRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Tax Amount
  const taxRow = ws.addRow([
    'Tax Amount',
    data.costSummary.taxAmount,
  ]);
  taxRow.eachCell((cell) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Grand Total
  const grandTotalRow = ws.addRow([
    'Grand Total',
    `${data.costSummary.grandTotal} ${data.costSummary.currency}`,
  ]);
  grandTotalRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  ws.addRow([]);


  const shipHeader = ws.addRow(['Summary']);
  shipHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'f9bccb' } };
  shipHeader.getCell(1).font = { bold: true };
  ws.addRow([]);
  ws.addRow(['Total Packages', data.packagingDetails?.totalPackages || data.lineItems.length]);
  ws.addRow(['Total Net Weight', data.packagingDetails?.totalNetWeight ? `${data.packagingDetails.totalNetWeight} kg` : '']);
  ws.addRow(['Dimensions', data.packagingDetails?.dimensions || '']);

  ws.addRow([]);
  ws.addRow([]);





  const authHeader = ws.addRow(['Perpared By']);
  authHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FCE4D6' } };
  authHeader.getCell(1).font = { bold: true };
  ws.addRow([]);
  ws.addRow(['Name']);
  ws.addRow(['Signature']);
  ws.addRow([]);
  ws.addRow([]);

  // // === VSMPS Buyer and Supplier section ===
  // ws.addRow([`From VSMPS Supplier:`]);
  // ws.addRow([data.supplierDetails.name]);
  // ws.addRow([data.supplierDetails.address]);
  // ws.addRow([]);

  // ws.addRow([`To VSMPS Buyer:`]);
  // ws.addRow([data.buyerDetails.name]);
  // ws.addRow([data.buyerDetails.address]);

  // ws.addRow([]);

  ws.addRow([]);
  ws.addRow(['Copyright © Varuna Sentinels BV. All rights reserved.']);

  // === AUTO WIDTH ===
  const colCount = ws.actualColumnCount;
  for (let i = 1; i <= colCount; i++) {
    let maxLen = 12;
    ws.eachRow({ includeEmpty: true }, (r) => {
      const val = r.getCell(i).value;
      const len = val ? val.toString().length : 0;
      if (len > maxLen) maxLen = len;
    });
    ws.getColumn(i).width = Math.min(maxLen + 4, 40);
  }

  workbook.xlsx.writeBuffer().then(buffer => {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    FileSaver.saveAs(blob, fileName);
  });


 }

 async generatePackingList(data:any,fileName:string){

  const workbook = new Excel.Workbook();
  const ws = workbook.addWorksheet('Packing List');

  // === TITLE ===
  ws.mergeCells('A1:G1');
  const title = ws.getCell('A1');
  title.value = 'Packing List';
  title.font = { size: 18, bold: true, color: { argb: '1F4E78' } };
  title.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.addRow([]);



  // === BUYER AND SUPPLIER DETAILS ===
  const BsHeader = ws.addRow(['Shipper Details (Supplier)', '', '', 'Consignee Details (Buyer)']);
  BsHeader.eachCell(c => {
    c.font = { bold: true };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2CC' } };
  });

  ws.addRow([]);

  const buyerEntries = Object.entries(data.buyerDetails).map(([key, value]) => [
    labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()),
    value
  ]);

  const supplierEntries = Object.entries(data.supplierDetails).map(([key, value]) => [
    labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()),
    value
  ]);

  // Pad so both arrays have the same length
  const maxRows = Math.max(buyerEntries.length, supplierEntries.length);
  const paddedBuyer = [...buyerEntries, ...Array(maxRows - buyerEntries.length).fill(['', ''])];
  const paddedSupplier = [...supplierEntries, ...Array(maxRows - supplierEntries.length).fill(['', ''])];


  for (let i = 0; i < maxRows; i++) {
    const row = ws.addRow([
      paddedBuyer[i][0], paddedBuyer[i][1],
      '',
      paddedSupplier[i][0], paddedSupplier[i][1]
    ]);


    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', wrapText: true };
    });
  }


  ws.addRow([]);
  ws.addRow([]);

     // === QUOTATION DETAILS ===
const shipmentHeader = ws.addRow(['Shipment Information']);
shipmentHeader.getCell(1).fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'D9E1F2' },
};
shipmentHeader.getCell(1).font = { bold: true };
ws.addRow([]);

// Quotation Info
const poInfo = [
  [
    'Vessel Name:',
    data.vesselDetails.vesselName,
    '',
    'Packing List Date:',
    data.invoiceDetails.invoiceDate,
  ],
  [
    'Port:',
    data.portDetails.portName,
    '',

    'Purchase Order Ref:',
    data.invoiceDetails.purchaseOrderNumber,
  ],
  [
    'Expected Delivery Date:',
    data.portDetails.requestedDeliveryDate,
    '',

    'Commercial Invoice Ref:',
    data.invoiceDetails.invoiceNumber,
  ],
  [
    'Freight Forwarder Company Name:',
    data.freightForwarderDetails.name,
    '',
    'Port Agent Company Name:',
    data.portAgentDetails.name,
  ],
  [
    'Freight Forwarder Address:',
    data.freightForwarderDetails.address,
    '',
    'Port Agent Address:',
    data.portAgentDetails.address,
  ],
  [
    'Incoterms:',
    data.portDetails.incoterms,
    '',
    '',
    '',
  ]
];
poInfo.forEach((r) => {
  const row = ws.addRow(r);
  row.eachCell((c) => (c.alignment = { vertical: 'middle' }));
});
ws.addRow([]);
ws.addRow([]);



  // === LINE ITEMS ===
  const liHeader = ws.addRow(['Line Items']);
  liHeader.getCell(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'BDD7EE' },
  };
  liHeader.getCell(1).font = { bold: true };
  ws.addRow([]);

  const tableHeader = ws.addRow([
    'Sr. No',
    'Part Number',
    'Item Description',
    'Requested Qty',
    'Requested UOM',
    'Unit Cost',
    'Discount',
    'Discounted Unit Cost',
    'Total Cost'
  ]);
  tableHeader.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'D9D9D9' },
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Add Line Items
  data.lineItems.forEach((item: {
    discountedUnitCost: any;
    supplierRemarks: any; srNo: any; partNumber: any; supplierPartNumber: any; itemDescription: any; requestedQty: any; quotedQuantity: any; requestedUom: any; quotedUom: any; unitCost: any; discount: any; totalCost: any; leadDays: any; supplierComment: any;
}) => {
    const row = ws.addRow([
      item.srNo,
      item.partNumber,
      item.itemDescription,
      item.requestedQty,
      item.requestedUom,
      item.unitCost,
      item.discount,
      item.discountedUnitCost,
      item.totalCost

    ]);
    row.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
      cell.alignment = { wrapText: true, vertical: 'middle' };
    });
  });

  ws.addRow([]);



  const costSummaryHeader = ws.addRow(['Cost Summary']);
  costSummaryHeader.getCell(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'F9F9F9' },
  };
  costSummaryHeader.getCell(1).font = { bold: true };
  ws.addRow([]);

  // Cost Summary Table
  const costSummaryTableHeader = ws.addRow(['Description', 'Amount']);
  costSummaryTableHeader.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'D9D9D9' },
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Line Total Amount
  const lineTotalRow = ws.addRow([
    'Line Total Amount',
    data.costSummary.lineItemSubtotal,
  ]);
  lineTotalRow.eachCell((cell) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // General Discount Amount
  const discountRow = ws.addRow([
    'General Discount Amount',
    data.costSummary.discountAmount,
  ]);
  discountRow.eachCell((cell) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Adding Additional Charges
  let totalAdditionalCharges = 0;
  data.costSummary.otherCharges.forEach((charge: { description: any; value: number; }) => {
    const row = ws.addRow([charge.description, charge.value]);
    row.eachCell((cell) => {
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });
    totalAdditionalCharges += charge.value;
  });

  // Total Additional Cost
  const totalAdditionalCostRow = ws.addRow([
    'Total Additional Cost',
    data.costSummary.totalAdditionalCost,
  ]);
  totalAdditionalCostRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Tax Amount
  const taxRow = ws.addRow([
    'Tax Amount',
    data.costSummary.taxAmount,
  ]);
  taxRow.eachCell((cell) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Grand Total
  const grandTotalRow = ws.addRow([
    'Grand Total',
    `${data.costSummary.grandTotal} ${data.costSummary.currency}`,
  ]);
  grandTotalRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  ws.addRow([]);


  const shipHeader = ws.addRow(['Summary']);
  shipHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'f9bccb' } };
  shipHeader.getCell(1).font = { bold: true };
  ws.addRow([]);
  ws.addRow(['Total Packages', data.packagingDetails?.totalPackages || data.lineItems.length]);
  ws.addRow(['Total Net Weight', data.packagingDetails?.totalNetWeight ? `${data.packagingDetails.totalNetWeight} kg` : '']);
  ws.addRow(['Dimensions', data.packagingDetails?.dimensions || '']);

  ws.addRow([]);
  ws.addRow([]);





  const authHeader = ws.addRow(['Perpared By']);
  authHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FCE4D6' } };
  authHeader.getCell(1).font = { bold: true };
  ws.addRow([]);
  ws.addRow(['Name']);
  ws.addRow(['Signature']);
  ws.addRow([]);
  ws.addRow([]);

  // === VSMPS Buyer and Supplier section ===
  // ws.addRow([`From VSMPS Supplier:`]);
  // ws.addRow([data.supplierDetails.name]);
  // ws.addRow([data.supplierDetails.address]);
  // ws.addRow([]);

  // ws.addRow([`To VSMPS Buyer:`]);
  // ws.addRow([data.buyerDetails.name]);
  // ws.addRow([data.buyerDetails.address]);

  // ws.addRow([]);

  ws.addRow([]);
  ws.addRow(['Copyright © Varuna Sentinels BV. All rights reserved.']);

  // === AUTO WIDTH ===
  const colCount = ws.actualColumnCount;
  for (let i = 1; i <= colCount; i++) {
    let maxLen = 12;
    ws.eachRow({ includeEmpty: true }, (r) => {
      const val = r.getCell(i).value;
      const len = val ? val.toString().length : 0;
      if (len > maxLen) maxLen = len;
    });
    ws.getColumn(i).width = Math.min(maxLen + 4, 40);
  }

  workbook.xlsx.writeBuffer().then(buffer => {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    FileSaver.saveAs(blob, fileName);
  });


 }


}
